import { ProjectPhase } from "../../../../../constants/project-phase/ProjectPhase";
import { SelectData } from "../../../../../models";
import { GetTaskBoardResponse } from "../../../../../service/query";
import { normalizeStage, stringKeyToSentenceCase } from "../../../../../utils";

type TaskBoardWorkflow = GetTaskBoardResponse["results"][number];

/**
 * A workflow hangs off a project or a deal — never a landholding (a row's `groupUuid` is its *project's*
 * landholding, which is why the New-task chooser offers a landholding for a general task only). The two
 * parents are therefore exhaustive, so this agrees with the tasks board's own `projectUuid === null ⇒
 * deal` (`TasksBoardContext`); it is tested positively on `dealUuid` only because that is the parent the
 * label actually depends on.
 */
const isDealWorkflow = (workflow: TaskBoardWorkflow): boolean => workflow.dealUuid != null;

/**
 * The phase/stage options for creating a task against one workflow.
 *
 * Built from the workflow's own **steps**, not from a static phase enum: a deal workflow's stages are
 * nothing like `ProjectPhase`, which is what the overlay falls back to when a host passes no options —
 * that fallback is why a deal used to offer project phases and then no steps to go with them.
 *
 * The keys are the raw `step.phase` strings rather than normalised stage keys, because the overlay's
 * step picker (`useTaskStepOptions`) matches `step.phase === phase` exactly. Deriving the options from
 * the steps therefore guarantees every offered phase has steps behind it — which is also all a task
 * create can use, since a workflow task has to hang off a step.
 *
 * Order follows the workflow's declared `phases` where they line up (matched on the normalised key, as
 * the two can differ in spacing/casing), and the steps' own sequence otherwise.
 */
export const buildWorkflowStageOptions = (workflow: TaskBoardWorkflow | undefined): SelectData => {
  if (!workflow?.steps?.length) return [];

  const phaseSequences = new Map<string, number>();
  workflow.phases?.forEach((phase) => phaseSequences.set(normalizeStage(phase.phase), phase.sequence));

  // First step seen per phase — its sequence orders phases the declared list doesn't cover.
  const firstStepSequences = new Map<string, number>();
  workflow.steps.forEach((step) => {
    if (step.phase == null || firstStepSequences.has(step.phase)) return;

    firstStepSequences.set(step.phase, step.sequence);
  });

  return Array.from(firstStepSequences.keys())
    .sort((a, b) => {
      const sequenceA = phaseSequences.get(normalizeStage(a)) ?? firstStepSequences.get(a) ?? 0;
      const sequenceB = phaseSequences.get(normalizeStage(b)) ?? firstStepSequences.get(b) ?? 0;

      return sequenceA - sequenceB;
    })
    .map((phase) => ({ key: phase, value: stringKeyToSentenceCase(phase) }));
};

/** Deals have stages, projects have phases — the overlay labels its picker with whichever applies. */
export const getWorkflowStageLabel = (workflow: TaskBoardWorkflow | undefined): string =>
  workflow && isDealWorkflow(workflow) ? "Stage" : "Phase";

/**
 * Whether a task in this stage may repeat. Only an ongoing-monitoring phase does, and only outside a deal
 * — a deal's stages have no equivalent, which is the same rule the tasks board applies.
 */
export const buildIsRepeatableWorkflowStage =
  (workflow: TaskBoardWorkflow | undefined) =>
  (stage: string | null | undefined): boolean =>
    workflow != null &&
    !isDealWorkflow(workflow) &&
    normalizeStage(stage) === normalizeStage(ProjectPhase.ongoingMonitoring);
